<script setup>
// SiteUp — Modo Foco ativo: cluster arrastável (posição em % da viewport).
// A forma é sempre vertical. Só o menu (e a seta) mudam de lado.
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  provide,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  coachForRoute,
  routeViewOf,
} from 'dashboard/helper/onboardingRegistry';
import { tourForRoute } from 'dashboard/helper/onboardingTours';
import { openPageManual } from 'dashboard/composables/usePageCoach';
import {
  requestTourReplay,
  tourFinishedToken,
} from 'dashboard/composables/useGuidedTour';
import {
  useFocusMode,
  resolveFocusMenuSide,
} from 'dashboard/composables/useFocusMode';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useTasksBellBadge } from 'dashboard/composables/useTasksBellBadge';
import { emitter } from 'shared/helpers/mitt';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import FocusModeSettings from './FocusModeSettings.vue';
import FocusModeGuide from './FocusModeGuide.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import SidebarTasksBell from './SidebarTasksBell.vue';
import SidebarNotificationBell from './SidebarNotificationBell.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';

const emit = defineEmits(['showCreateAccountModal', 'openKeyShortcutModal']);

const COMPOSER_SELECTOR = '[data-conversation-composer], .reply-box';
const COMPOSER_GAP_PX = 12;
const DRAG_THRESHOLD_PX = 6;
const DEFAULT_INSET_PX = 24;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { enabled: helpEnabled } = useOnboardingSurface();
const hasScreenManual = computed(
  () =>
    helpEnabled.value &&
    Boolean(coachForRoute(route?.name, routeViewOf(route))?.manualKey)
);
const screenTour = computed(() =>
  helpEnabled.value ? tourForRoute(route?.name, routeViewOf(route)) : null
);

const { accountScopedRoute } = useAccount();
const hubRoute = computed(() => accountScopedRoute('focus_hub'));
// Core do sistema no ícone do hub (Lucas, 03/09/2026): Tarefas, Kanban e
// Agenda sempre à mão, no menu e na fila de atalhos. Espelha a primeira linha
// do HubView.
const tasksRoute = computed(() => accountScopedRoute('tasks'));
const kanbanRoute = computed(() => accountScopedRoute('kanban_board'));
const kanbanAgendaRoute = computed(() => ({
  ...accountScopedRoute('kanban_board'),
  query: { view: 'agenda' },
}));
const {
  shouldShowWelcome,
  dockPosition,
  saveDockPosition,
  clearDockPosition,
  menuSide,
  isClusterCollapsed,
  setClusterCollapsed,
} = useFocusMode();
const { listMode, setListMode } = useWorkbenchLayout();
// Só para o ponto do botão principal quando o cluster está recolhido e o sino
// saiu da tela. Mesma fonte do badge do sino.
const { badge } = useTasksBellBadge();

const settingsRef = ref(null);
const guideRef = ref(null);
const clusterRef = ref(null);
const menuRef = ref(null);
const menuToggleRef = ref(null);
const isMenuOpen = ref(false);
const isFullscreen = ref(false);
const isMoving = ref(false);
const livePosition = ref(null);
const effectiveSide = ref('up');

// O Brasa e o botao do menu: parado, respira; com o menu aberto, olha pro
// lado em que o menu abriu. Pedido do Lucas em 23/08 (o mascote e o menu).
const hasPendingBadge = computed(() => Boolean(badge.value));

// O Brasa do botao reage ao que acontece, em vez de ficar parado: arrastando
// ele voa, com aviso novo ele sacuda, com fila esperando ele fica olhando em
// volta, no hover bate asa, e comemora quando a pessoa termina um tour.
// Pedido do Lucas em 23/08/2026 ("dar uma animada dependendo a acao").
const NUDGE_MS = 700;
const CELEBRATE_MS = 1400;
const mascotHover = ref(false);
const mascotPulse = ref(null);
let pulseTimer = null;

const pulse = (state, ms) => {
  if (typeof window === 'undefined') return;
  window.clearTimeout(pulseTimer);
  mascotPulse.value = state;
  pulseTimer = window.setTimeout(() => {
    mascotPulse.value = null;
    pulseTimer = null;
  }, ms);
};

const mascotState = computed(() => {
  if (isMoving.value) return 'fly';
  if (mascotPulse.value) return mascotPulse.value;
  if (mascotHover.value) return 'hover';
  if (hasPendingBadge.value) return 'wait';
  return 'idle';
});

const MENU_SIDE_BEARING = { up: 0, right: 90, down: 180, left: 270 };
// Última medida real do menu do cluster (reusada na seta com o menu fechado).
const lastMenuSize = ref(null);

let pointerOffset = { x: 0, y: 0 };
let pointerStart = { x: 0, y: 0 };
let didDrag = false;

const clusterSize = () => ({
  width: clusterRef.value?.offsetWidth || 54,
  height: clusterRef.value?.offsetHeight || 200,
});

const defaultPosition = () => {
  if (typeof window === 'undefined') return { xPct: 0.86, yPct: 0.04 };
  const { width } = clusterSize();
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  return {
    xPct: Math.max(0, (vw - DEFAULT_INSET_PX - width) / vw),
    yPct: DEFAULT_INSET_PX / vh,
  };
};

const liveComposerRect = () => {
  if (typeof document === 'undefined') return null;
  const composer = document.querySelector(COMPOSER_SELECTOR);
  return composer ? composer.getBoundingClientRect() : null;
};

// A garantia "nunca estacionar sobre o compositor" vale no descanso, não
// durante o gesto: no gesto ela virava um trilho horizontal.
const clampPosition = (pos, { composer = true } = {}) => {
  if (typeof window === 'undefined') return pos;
  const { width, height } = clusterSize();
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  const maxX = Math.max(0, vw - width);
  const maxY = Math.max(0, vh - height);
  let x = Math.min(maxX, Math.max(0, pos.xPct * vw));
  let y = Math.min(maxY, Math.max(0, pos.yPct * vh));
  if (composer) {
    const composerRect = liveComposerRect();
    if (composerRect) {
      const overlapsX = x < composerRect.right && x + width > composerRect.left;
      const overlapsY =
        y < composerRect.bottom + COMPOSER_GAP_PX &&
        y + height + COMPOSER_GAP_PX > composerRect.top;
      if (overlapsX && overlapsY) {
        const ceiling = Math.max(
          0,
          composerRect.top - height - COMPOSER_GAP_PX
        );
        if (y > ceiling) y = ceiling;
      }
    }
  }
  return { xPct: x / vw, yPct: y / vh };
};

const resolvedPosition = computed(() => {
  if (livePosition.value) {
    return clampPosition(livePosition.value, { composer: false });
  }
  return clampPosition(dockPosition.value || defaultPosition());
});

const clusterStyle = computed(() => {
  const pos = resolvedPosition.value;
  return {
    left: `${pos.xPct * 100}%`,
    top: `${pos.yPct * 100}%`,
    right: 'auto',
    bottom: 'auto',
  };
});

const readOpenMenuSize = () => {
  const menu = menuRef.value;
  if (!menu) return lastMenuSize.value || {};
  const size = {
    width: Math.max(menu.offsetWidth || 0, menu.scrollWidth || 0),
    height: Math.max(menu.scrollHeight || 0, menu.offsetHeight || 0),
  };
  if (size.width > 0 && size.height > 0) lastMenuSize.value = size;
  return lastMenuSize.value || size;
};

const syncEffectiveSide = menuSize => {
  const rect = clusterRef.value?.getBoundingClientRect();
  if (!rect || typeof window === 'undefined') return;
  effectiveSide.value = resolveFocusMenuSide(
    menuSide.value,
    rect,
    { width: window.innerWidth, height: window.innerHeight },
    menuSize || lastMenuSize.value || {}
  );
};

// A seta deixou de apontar para onde o menu abre e passou a abrir/recolher o
// cluster. Os itens ficam SEMPRE abaixo do botão principal (a forma é
// vertical), então o sentido não depende do lado do menu: baixo revela,
// cima esconde. Quem abre o menu agora é o botão principal.
const mascotLook = computed(() => {
  if (isMenuOpen.value) return MENU_SIDE_BEARING[effectiveSide.value] ?? null;
  // Parado no canto, ele olha pro meio da tela (de onde a pessoa trabalha).
  if (mascotState.value === 'idle' || mascotState.value === 'wait') {
    return resolvedPosition.value.xPct > 0.5 ? 270 : 90;
  }
  return null;
});

const arrowIcon = computed(() =>
  isClusterCollapsed.value ? 'i-ri-arrow-down-s-line' : 'i-ri-arrow-up-s-line'
);

const collapseLabel = computed(() =>
  isClusterCollapsed.value
    ? t('SIDEBAR.FOCUS_MODE.EXPAND_CLUSTER')
    : t('SIDEBAR.FOCUS_MODE.COLLAPSE_CLUSTER')
);

const toggleCollapsed = () => {
  setClusterCollapsed(!isClusterCollapsed.value);
  // Recolher muda a altura do cluster, e o lado do menu é decidido pela medida
  // dele. Sem re-sincronizar, o menu abriria com a altura antiga — mesmo
  // motivo pelo qual o resize já faz isso.
  nextTick(() => syncEffectiveSide());
};

const menuAlignsEnd = computed(() => resolvedPosition.value.xPct > 0.5);

const menuPositionClass = computed(() => {
  const align = menuAlignsEnd.value ? 'right-0' : 'left-0';
  if (effectiveSide.value === 'down') return `top-full mt-2 ${align}`;
  if (effectiveSide.value === 'left') return 'right-full mr-2';
  if (effectiveSide.value === 'right') return 'left-full ml-2';
  return `bottom-full mb-2 ${align}`;
});

const hasCustomDock = computed(() => Boolean(dockPosition.value));

const menuStyle = computed(() => {
  if (typeof window === 'undefined') return {};
  const rect = clusterRef.value?.getBoundingClientRect();
  if (!rect) return {};
  const gap = 8;
  const side = effectiveSide.value;
  // Piso de rolagem: menu cortado sem scroll é pior que menu com scroll.
  const MIN_SCROLL = 120;
  if (side === 'up') {
    return { maxHeight: `${Math.max(MIN_SCROLL, rect.top - gap)}px` };
  }
  if (side === 'down') {
    return {
      maxHeight: `${Math.max(MIN_SCROLL, window.innerHeight - rect.bottom - gap)}px`,
    };
  }
  const spaceBelow = window.innerHeight - rect.top;
  const spaceAbove = rect.bottom;
  return {
    maxHeight: `${Math.max(MIN_SCROLL, window.innerHeight - 16)}px`,
    ...(spaceBelow >= spaceAbove
      ? { top: '0px', bottom: 'auto' }
      : { bottom: '0px', top: 'auto' }),
  };
});

const syncFullscreen = () => {
  isFullscreen.value = Boolean(document.fullscreenElement);
};

const toggleFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen?.();
  } else {
    document.documentElement.requestFullscreen?.();
  }
};

const menuFocusables = () =>
  [...(menuRef.value?.querySelectorAll('a[href],button') || [])].filter(
    el => !el.disabled
  );

const closeMenu = () => {
  isMenuOpen.value = false;
};

// Agenda: com a flag agenda_central (Lia) abre a Central por cima de qualquer
// tela; sem ela, abre a visão de agenda do Kanban. Só some quando a conta não
// tem nem uma nem outra. Kanban entra pela própria flag.
const { isFeatureFlagEnabled } = usePolicy();
const hasAgendaCentral = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.AGENDA_CENTRAL)
);
const showKanbanItem = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.KANBAN_BOARD)
);
const showAgendaItem = computed(
  () => hasAgendaCentral.value || showKanbanItem.value
);
const openAgendaCentral = () => {
  if (hasAgendaCentral.value) {
    emitter.emit('agenda:quick:open');
  } else {
    router.push(kanbanAgendaRoute.value);
  }
  closeMenu();
};

const openMenu = () => {
  // 1ª passada com a última medida (ou fallback) para a seta/lado iniciais.
  syncEffectiveSide();
  isMenuOpen.value = true;
  // 2ª passada: menu já no DOM → mede tamanho real e reposiciona se precisar.
  nextTick(() => {
    syncEffectiveSide(readOpenMenuSize());
    menuFocusables()[0]?.focus();
  });
};

const toggleMenu = () => {
  if (didDrag) return;
  if (isMenuOpen.value) closeMenu();
  else openMenu();
};

provide('focusModeGuideHost', {
  openMenu,
  closeMenu,
});
provide('startFocusModeTour', () => {
  closeMenu();
  settingsRef.value?.close();
  nextTick(() => guideRef.value?.startTour());
});

const onDocClick = event => {
  if (!isMenuOpen.value) return;
  if (clusterRef.value?.contains(event.target)) return;
  if (event.target.closest?.('[data-testid="focus-mode-tour"]')) return;
  if (event.target.closest?.('[data-testid="focus-mode-welcome"]')) return;
  if (event.target.closest?.('[data-testid="focus-mode-settings"]')) return;
  if (event.target.closest?.('[data-sidebar-account-menu]')) return;
  if (event.target.closest?.('[data-sidebar-profile-menu]')) return;
  if (event.target.closest?.('[data-sidebar-tasks-menu]')) return;
  closeMenu();
};

const onDocKeydown = event => {
  if (event.key !== 'Escape' || !isMenuOpen.value) return;
  closeMenu();
  menuToggleRef.value?.focus();
};

const onMenuKeydown = event => {
  if (event.key !== 'Tab' || !isMenuOpen.value) return;
  const items = menuFocusables();
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return;
  }
  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

// Foco total e o unico modo de lista que mora no menu (pedido do Lucas em
// 23/08): Lista e So fotos sairam daqui, ficam em Personalizar Modo Foco.
// Ligado, a lista some e as conversas passam uma a uma; desligado, volta a
// lista normal.
// Aviso novo chegando: sacode uma vez. So quando o numero SOBE, para nao
// sacudir ao marcar como visto.
watch(
  () => Number(String(badge.value).replace('+', '')) || 0,
  (agora, antes) => {
    if (agora > (antes || 0)) pulse('nudge', NUDGE_MS);
  }
);

// Terminou um tour: comemoracao curta no botao.
watch(tourFinishedToken, () => pulse('celebrate', CELEBRATE_MS));

const isFocoTotal = computed(() => listMode.value === 'hidden');
const toggleFocoTotal = () => {
  setListMode(isFocoTotal.value ? 'list' : 'hidden');
};

const openSettings = () => {
  closeMenu();
  settingsRef.value?.open();
};

const openScreenManual = () => {
  closeMenu();
  openPageManual();
};

const replayScreenTour = () => {
  const key = screenTour.value?.key;
  if (!key) return;
  closeMenu();
  requestTourReplay(key);
};

const resetDockToDefault = () => {
  livePosition.value = null;
  clearDockPosition();
  closeMenu();
};

const onPointerMove = event => {
  const dx = event.clientX - pointerStart.x;
  const dy = event.clientY - pointerStart.y;
  if (!didDrag && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
  if (!didDrag) {
    didDrag = true;
    isMoving.value = true;
    closeMenu();
  }
  const { width, height } = clusterSize();
  const x = event.clientX - pointerOffset.x;
  const y = event.clientY - pointerOffset.y;
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  const maxX = Math.max(0, vw - width);
  const maxY = Math.max(0, vh - height);
  livePosition.value = clampPosition(
    {
      xPct: Math.min(maxX, Math.max(0, x)) / vw,
      yPct: Math.min(maxY, Math.max(0, y)) / vh,
    },
    { composer: false }
  );
  nextTick(syncEffectiveSide);
};

const stopMoving = () => {
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopMoving);
  window.removeEventListener('pointercancel', stopMoving);
  isMoving.value = false;
  if (didDrag && livePosition.value) {
    saveDockPosition(clampPosition(livePosition.value));
  }
  livePosition.value = null;
  nextTick(syncEffectiveSide);
  // didDrag precisa sobreviver ao pointerup para o clique do arrasto ser
  // cancelado logo em seguida, mas o clique nem sempre chega: soltar o
  // ponteiro fora do cluster deixava a flag presa e o PRÓXIMO clique de
  // qualquer botão daqui era engolido. Limpa no fim da fila de eventos.
  setTimeout(() => {
    didDrag = false;
  }, 0);
};

const onPointerDown = event => {
  if (event.button !== 0) return;
  if (menuRef.value?.contains(event.target)) return;
  const rect = clusterRef.value?.getBoundingClientRect();
  if (!rect) return;
  pointerStart = { x: event.clientX, y: event.clientY };
  pointerOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  didDrag = false;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopMoving);
  window.addEventListener('pointercancel', stopMoving);
};

const onClusterClickCapture = event => {
  if (!didDrag) return;
  event.preventDefault();
  event.stopPropagation();
  didDrag = false;
};

const onWindowResize = () => {
  nextTick(syncEffectiveSide);
};

watch(menuSide, () => nextTick(syncEffectiveSide));

onMounted(() => {
  syncFullscreen();
  nextTick(syncEffectiveSide);
  document.addEventListener('fullscreenchange', syncFullscreen);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeydown);
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  if (pulseTimer) window.clearTimeout(pulseTimer);
  document.removeEventListener('fullscreenchange', syncFullscreen);
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onDocKeydown);
  window.removeEventListener('resize', onWindowResize);
  stopMoving();
});
</script>

<template>
  <div>
    <div
      ref="clusterRef"
      class="fixed z-[60] flex flex-col items-center gap-0.5 rounded-full border border-n-weak/60 bg-n-solid-1/80 p-1 shadow-lg backdrop-blur-xl select-none touch-none [-webkit-user-drag:none]"
      :class="[isMoving ? 'cursor-grabbing' : 'cursor-grab']"
      :style="clusterStyle"
      data-testid="focus-mode-cluster"
      @dragstart.prevent
      @pointerdown="onPointerDown"
      @click.capture="onClusterClickCapture"
    >
      <div
        v-if="isMenuOpen"
        id="focus-mode-menu"
        ref="menuRef"
        class="absolute z-10 w-56 overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-1.5 shadow-xl"
        :class="menuPositionClass"
        :style="menuStyle"
        data-testid="focus-mode-menu"
        :data-side="effectiveSide"
        :aria-label="t('SIDEBAR.FOCUS_MODE.TITLE')"
        @keydown="onMenuKeydown"
      >
        <RouterLink
          :to="hubRoute"
          draggable="false"
          class="flex min-h-11 items-center gap-2.5 rounded-xl px-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-home"
          @click="closeMenu"
        >
          <span class="i-ri-apps-2-line size-4 shrink-0 text-n-slate-11" />
          {{ t('HUB.TITLE') }}
        </RouterLink>

        <RouterLink
          :to="tasksRoute"
          draggable="false"
          class="flex min-h-11 items-center gap-2.5 rounded-xl px-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-tasks-link"
          @click="closeMenu"
        >
          <span class="i-ri-check-line size-4 shrink-0 text-n-slate-11" />
          {{ t('SIDEBAR.FOCUS_MODE.TASKS') }}
        </RouterLink>

        <RouterLink
          v-if="showKanbanItem"
          :to="kanbanRoute"
          draggable="false"
          class="flex min-h-11 items-center gap-2.5 rounded-xl px-2.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-kanban"
          @click="closeMenu"
        >
          <span class="i-ri-layout-grid-line size-4 shrink-0 text-n-slate-11" />
          {{ t('SIDEBAR.FOCUS_MODE.KANBAN') }}
        </RouterLink>

        <button
          v-if="showAgendaItem"
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-agenda"
          @click="openAgendaCentral"
        >
          <span class="i-ri-calendar-2-line size-4 shrink-0 text-n-slate-11" />
          {{ t('SIDEBAR.FOCUS_MODE.AGENDA') }}
        </button>

        <button
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-fullscreen"
          @click="toggleFullscreen"
        >
          <span
            class="size-4 shrink-0 text-n-slate-11"
            :class="
              isFullscreen
                ? 'i-ri-fullscreen-exit-line'
                : 'i-ri-fullscreen-line'
            "
          />
          {{
            isFullscreen
              ? t('SIDEBAR.FOCUS_MODE.EXIT_FULLSCREEN')
              : t('SIDEBAR.FOCUS_MODE.FULLSCREEN')
          }}
        </button>

        <div class="my-1 border-t border-n-weak" />

        <button
          type="button"
          role="switch"
          :aria-checked="isFocoTotal"
          data-testid="focus-mode-mode-hidden"
          class="flex min-h-12 w-full items-center gap-2.5 rounded-xl px-2.5 text-left transition-colors hover:bg-n-alpha-2"
          :class="isFocoTotal ? 'bg-n-iris-9/15' : ''"
          @click="toggleFocoTotal"
        >
          <span
            class="i-ri-aspect-ratio-line size-4 shrink-0"
            :class="isFocoTotal ? 'text-n-iris-11' : 'text-n-slate-11'"
          />
          <span class="flex min-w-0 flex-1 flex-col">
            <span
              class="text-sm font-semibold"
              :class="isFocoTotal ? 'text-n-iris-11' : 'text-n-slate-12'"
            >
              {{ t('CHAT_LIST.WORKBENCH.MODE_HIDDEN') }}
            </span>
            <span class="text-[11px] leading-tight text-n-slate-11">
              {{ t('CHAT_LIST.WORKBENCH.MODE_HIDDEN_HINT') }}
            </span>
          </span>
          <span
            class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
            :class="isFocoTotal ? 'bg-n-iris-9' : 'bg-n-alpha-3'"
            aria-hidden="true"
          >
            <span
              class="absolute size-4 rounded-full bg-white shadow transition-[left]"
              :class="isFocoTotal ? 'left-[18px]' : 'left-0.5'"
            />
          </span>
        </button>

        <div class="my-1 border-t border-n-weak" />

        <button
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-customize"
          @click="openSettings"
        >
          <span class="i-ri-settings-2-line size-4 shrink-0 text-n-slate-11" />
          {{ t('SIDEBAR.FOCUS_MODE.CUSTOMIZE') }}
        </button>

        <button
          v-if="hasScreenManual"
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="page-manual-open"
          @click="openScreenManual"
        >
          <span class="i-ri-book-open-line size-4 shrink-0 text-n-slate-11" />
          {{ t('ONBOARDING.MANUAL.CLUSTER') }}
        </button>

        <button
          v-if="screenTour"
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="guided-tour-replay"
          @click="replayScreenTour"
        >
          <span class="i-ri-refresh-line size-4 shrink-0 text-n-slate-11" />
          {{ t('ONBOARDING.TOURS.CLUSTER_REPLAY') }}
        </button>

        <button
          v-if="hasCustomDock"
          type="button"
          class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          data-testid="focus-mode-reset-position"
          @click="resetDockToDefault"
        >
          <span
            class="i-ri-arrow-go-back-line size-4 shrink-0 text-n-slate-11"
          />
          {{ t('SIDEBAR.FOCUS_MODE.RESET_POSITION') }}
        </button>
      </div>

      <button
        ref="menuToggleRef"
        v-tooltip="t('SIDEBAR.FOCUS_MODE.REOPEN')"
        type="button"
        draggable="false"
        class="relative flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-n-solid-2 text-white shadow-[0_0_0_4px_rgb(var(--iris-9)/0.28)] transition-transform duration-150 hover:bg-n-solid-3 active:scale-95"
        :class="{
          'ring-2 ring-n-iris-9 ring-offset-2 ring-offset-n-solid-1':
            shouldShowWelcome,
        }"
        data-testid="focus-mode-menu-toggle"
        :aria-expanded="isMenuOpen"
        aria-haspopup="true"
        aria-controls="focus-mode-menu"
        :aria-label="t('SIDEBAR.FOCUS_MODE.REOPEN')"
        @click="toggleMenu"
        @pointerenter="mascotHover = true"
        @pointerleave="mascotHover = false"
      >
        <BrasaMascot
          class="pointer-events-none"
          :size="36"
          :state="mascotState"
          :look="mascotLook"
          data-testid="focus-mode-menu-mascot"
        />
        <!-- Recolhido, o sino sai da tela; o ponto devolve o aviso sem
             ocupar slot. Expandido, quem mostra o número é o próprio sino. -->
        <span
          v-if="isClusterCollapsed && hasPendingBadge"
          class="absolute -right-0.5 -top-0.5 size-3 rounded-full border-2 border-n-solid-1 bg-n-ruby-9"
          data-testid="focus-mode-collapsed-badge"
          :aria-label="t('SIDEBAR.FOCUS_MODE.COLLAPSED_BADGE')"
        />
      </button>

      <button
        v-tooltip="collapseLabel"
        type="button"
        class="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
        data-testid="focus-mode-collapse-toggle"
        :aria-expanded="!isClusterCollapsed"
        aria-controls="focus-mode-cluster-items"
        :aria-label="collapseLabel"
        @click="toggleCollapsed"
      >
        <span class="size-5" :class="arrowIcon" />
      </button>

      <div
        v-if="!isClusterCollapsed"
        id="focus-mode-cluster-items"
        class="contents"
        @pointerdown.stop
      >
        <div
          class="animate-cluster-in motion-reduce:animate-none"
          data-testid="focus-mode-cluster-item"
        >
          <SidebarAccountSwitcher
            variant="icon"
            placement="top"
            data-testid="focus-mode-account"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </div>
        <div
          class="animate-cluster-in delay-[20ms] motion-reduce:animate-none"
          data-testid="focus-mode-cluster-item"
        >
          <div data-testid="focus-mode-tasks">
            <SidebarTasksBell variant="icon" />
          </div>
        </div>
        <div
          v-if="showKanbanItem"
          class="animate-cluster-in delay-[25ms] motion-reduce:animate-none"
          data-testid="focus-mode-cluster-item"
        >
          <RouterLink
            v-tooltip="t('SIDEBAR.FOCUS_MODE.KANBAN')"
            :to="kanbanRoute"
            draggable="false"
            class="grid size-9 flex-shrink-0 cursor-pointer place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            data-testid="focus-mode-kanban-quick"
            :aria-label="t('SIDEBAR.FOCUS_MODE.KANBAN')"
          >
            <span class="i-ri-layout-grid-line size-4" />
          </RouterLink>
        </div>
        <div
          v-if="showAgendaItem"
          class="animate-cluster-in delay-[30ms] motion-reduce:animate-none"
          data-testid="focus-mode-cluster-item"
        >
          <button
            v-tooltip="t('SIDEBAR.FOCUS_MODE.AGENDA')"
            type="button"
            draggable="false"
            class="grid size-9 flex-shrink-0 cursor-pointer place-content-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            data-testid="focus-mode-agenda-quick"
            :aria-label="t('SIDEBAR.FOCUS_MODE.AGENDA')"
            @click="openAgendaCentral"
          >
            <span class="i-ri-calendar-2-line size-4" />
          </button>
        </div>
        <div
          class="animate-cluster-in delay-[40ms] motion-reduce:animate-none"
          data-testid="focus-mode-cluster-item"
        >
          <div data-testid="focus-mode-notifications">
            <SidebarNotificationBell variant="icon" />
          </div>
        </div>
        <div
          class="animate-cluster-in delay-[60ms] motion-reduce:animate-none"
          data-testid="focus-mode-cluster-item"
        >
          <SidebarProfileMenu
            is-collapsed
            data-testid="focus-mode-profile"
            @open-key-shortcut-modal="emit('openKeyShortcutModal')"
          />
        </div>
      </div>
    </div>

    <FocusModeGuide ref="guideRef" />
    <FocusModeSettings ref="settingsRef" />
  </div>
</template>
